import { cn } from '../../utils/cn'

export type StatusVariant =
  | 'draft'
  | 'extracting'
  | 'needs_review'
  | 'reviewing'
  | 'validated'
  | 'approved'
  | 'rejected'
  | 'exported'
  | 'archived'
  | 'completed'
  | 'failed'
  | 'running'
  | 'pending'
  | 'active'
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'error'
  | 'warning'
  | 'info'
  | 'success'

interface StatusBadgeProps {
  status: string
  variant?: StatusVariant
  size?: 'sm' | 'md'
  className?: string
}

const statusMap: Record<string, StatusVariant> = {
  draft: 'draft',
  needs_review: 'needs_review',
  reviewing: 'reviewing',
  validated: 'validated',
  approved: 'approved',
  rejected: 'rejected',
  exported: 'exported',
  archived: 'archived',
  active: 'active',
  completed: 'completed',
  failed: 'failed',
  running: 'running',
  pending: 'pending',
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
}

const styles: Record<StatusVariant, string> = {
  draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  extracting: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  needs_review: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  reviewing: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  validated: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  exported: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  archived: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  running: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
}

export function displayStatus(status: string): string {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function StatusBadge({ status, variant, size = 'sm', className }: StatusBadgeProps) {
  const effective = variant || statusMap[status.toLowerCase()] || 'info'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
        styles[effective],
        className
      )}
    >
      {displayStatus(status)}
    </span>
  )
}
